import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, inr, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { WishlistButton } from "@/components/WishlistButton";
import { Sparkles, Music, Tent, Utensils, Gift, Users, Check, Plus, Minus, SlidersHorizontal, X } from "lucide-react";
import { motion } from "framer-motion";

const TIER_COLORS = {
  Silver: "bg-slate-100 text-slate-700",
  Gold: "bg-amber-100 text-amber-700",
  Platinum: "bg-violet-100 text-violet-700",
};

export function computePackage(p, guests) {
  const items = p.items || [];
  const hasPerGuest = items.some((it) => it.per_guest);
  if (!items.length) return { hasPerGuest, subtotal: p.subtotal || 0, price: p.price };
  const subtotal = items.reduce((s, it) => s + (it.price || 0) * ((it.quantity || 1) * (it.per_guest ? guests : 1)), 0);
  const price = Math.round(subtotal * (1 - (p.discount_pct || 0) / 100));
  return { hasPerGuest, subtotal, price };
}

export function PackageCard({ p, index = 0 }) {
  const { addItem } = useCart();
  const minG = p.min_guests || 10;
  const maxG = p.max_guests && p.max_guests > minG ? p.max_guests : 100000;
  const clampG = (g) => Math.min(maxG, Math.max(minG, Number(g) || minG));
  const [guests, setGuests] = useState(clampG(p.base_guests || 100));
  const { hasPerGuest, subtotal, price } = computePackage(p, guests);
  const perHead = hasPerGuest && guests > 0 ? Math.round(price / guests) : 0;
  const bump = (d) => setGuests((g) => clampG((Number(g) || 0) + d));

  const addPackage = () => {
    const label = hasPerGuest ? `${p.name} Package (for ${guests} guests)` : `${p.name} Package`;
    addItem(
      { id: p.id, name: label, price, unit: "per event", image: p.image },
      { id: "bookmyevent-packages", name: "Book My Event" }
    );
  };

  return (
    <motion.div
      data-testid={`package-card-${p.id}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.06, 0.4) }}
      className="bg-card border border-stone-200 rounded-2xl overflow-hidden flex flex-col"
    >
      <div className="relative h-40">
        {p.image ? <img src={imgUrl(p.image)} alt={p.name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-primary/30 to-secondary/30" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full overline ${TIER_COLORS[p.tier] || "bg-white text-foreground"}`}>{p.tier}</span>
          {p.popular && <span className="text-[10px] font-bold px-2 py-1 rounded-full overline bg-secondary text-secondary-foreground">Popular</span>}
        </div>
        {p.discount_pct > 0 && subtotal > price && (
          <div className="absolute top-3 right-3 flex items-center gap-2" data-testid={`package-save-${p.id}`}>
            <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-emerald-500 text-white shadow-lg">Save {inr(subtotal - price)}</span>
            <WishlistButton kind="package" item={{ id: p.id, name: p.name, price, image: p.image }} />
          </div>
        )}
        {!(p.discount_pct > 0 && subtotal > price) && (
          <div className="absolute top-3 right-3">
            <WishlistButton kind="package" item={{ id: p.id, name: p.name, price, image: p.image }} />
          </div>
        )}
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-white font-display font-extrabold text-2xl leading-none">{p.name}</h3>
          <p className="text-white/85 text-xs mt-1">{p.tagline}</p>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-3">
          <span className="flex items-center gap-1 text-sm font-semibold text-muted-foreground">
            <Users className="h-4 w-4" /> {p.guest_label || (hasPerGuest ? `${guests} guests` : "All sizes")}
          </span>
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{p.event_type}</span>
        </div>

        <div className="grid gap-2 text-sm flex-1">
          {p.items?.length ? (
            <ul className="space-y-1.5">
              {p.items.map((it, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-foreground">
                    {it.name}
                    {it.per_guest ? ` × ${guests}` : it.quantity > 1 ? ` × ${it.quantity}` : ""}
                    {it.per_guest ? <span className="text-[10px] text-primary font-semibold ml-1">per guest</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <>
              <Inclusion icon={Sparkles} label="Decoration" text={p.decoration} />
              <Inclusion icon={Music} label="DJ & Sound" text={p.dj} />
              <Inclusion icon={Tent} label="Shamiyana" text={p.shamiyana} />
              <div className="flex gap-2">
                <Utensils className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-foreground">Menu</p>
                  <ul className="text-muted-foreground text-[13px] leading-relaxed">
                    {(p.menu || []).map((m) => <li key={m}>• {m}</li>)}
                  </ul>
                </div>
              </div>
              {(p.extras || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {p.extras.map((e) => (
                    <span key={e} className="text-[11px] font-medium bg-muted text-muted-foreground px-2 py-1 rounded-full flex items-center gap-1">
                      <Check className="h-3 w-3 text-emerald-600" /> {e}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {hasPerGuest && (
          <div className="mt-4 bg-muted/50 rounded-xl px-3 py-2.5" data-testid={`package-guest-picker-${p.id}`}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-sm font-semibold"><Users className="h-4 w-4 text-primary" /> Guests</span>
              <div className="flex items-center gap-2">
                <button data-testid={`package-guest-minus-${p.id}`} onClick={() => bump(-25)} className="h-8 w-8 grid place-items-center rounded-full border border-stone-300 hover:bg-white"><Minus className="h-4 w-4" /></button>
                <input
                  data-testid={`package-guests-${p.id}`}
                  type="number"
                  min={minG}
                  max={maxG < 100000 ? maxG : undefined}
                  value={guests}
                  onChange={(e) => setGuests(clampG(e.target.value))}
                  className="w-16 h-8 text-center rounded-md border border-input bg-card text-sm font-bold"
                />
                <button data-testid={`package-guest-plus-${p.id}`} onClick={() => bump(25)} className="h-8 w-8 grid place-items-center rounded-full border border-stone-300 hover:bg-white"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[11px] text-muted-foreground">
              <span>Min {minG}{maxG < 100000 ? ` · Max ${maxG}` : ""}</span>
              {perHead > 0 && <span data-testid={`package-perhead-${p.id}`} className="font-semibold text-primary">≈ {inr(perHead)} / guest</span>}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-200">
          <div>
            <p className="text-xs text-muted-foreground">{p.discount_pct > 0 ? `All-inclusive · ${p.discount_pct}% off` : "All-inclusive"}</p>
            <div className="flex items-baseline gap-2">
              {p.discount_pct > 0 && subtotal > price ? <span className="text-sm text-muted-foreground line-through">{inr(subtotal)}</span> : null}
              <p className="font-display font-extrabold text-2xl" data-testid={`package-price-${p.id}`}>{inr(price)}</p>
            </div>
          </div>
          <button
            data-testid={`add-package-${p.id}`}
            onClick={addPackage}
            className="flex items-center gap-2 h-11 px-5 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-5 w-5" /> Add to cart
          </button>
        </div>
      </div>
    </motion.div>
  );
}

const BUDGET_OPTIONS = [
  { label: "Any budget", value: 0 },
  { label: "Under ₹50,000", value: 50000 },
  { label: "Under ₹1,00,000", value: 100000 },
  { label: "Under ₹2,00,000", value: 200000 },
  { label: "Under ₹5,00,000", value: 500000 },
];

export default function Packages() {
  const { data: packages = [], isLoading } = useQuery({
    queryKey: ["packages"],
    queryFn: async () => (await api.get("/packages")).data,
  });

  const [occasion, setOccasion] = useState("All");
  const [maxBudget, setMaxBudget] = useState(0);

  const occasions = ["All", ...Array.from(new Set(packages.map((p) => p.event_type).filter(Boolean)))];
  const filtered = packages.filter((p) => {
    if (occasion !== "All" && (p.event_type || "") !== occasion) return false;
    if (maxBudget && (p.price || 0) > maxBudget) return false;
    return true;
  });
  const activeFilters = (occasion !== "All") + (maxBudget > 0);
  const clearFilters = () => { setOccasion("All"); setMaxBudget(0); };
  const selCls = "h-10 rounded-full border border-stone-200 bg-card px-4 text-sm font-medium focus:outline-none focus:border-primary";

  return (
    <div className="px-4 md:px-8 py-6 pb-24">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center">
          <Gift className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="overline text-primary">Book My Event exclusives</p>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Event Packages</h1>
        </div>
      </div>
      <p className="text-muted-foreground text-sm mb-5">
        All-inclusive bundles at a special price. Pick a package and set your guest count — the price adjusts automatically.
      </p>

      {!isLoading && packages.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="packages-filters">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground"><SlidersHorizontal className="h-4 w-4" /> Filters</span>
          <select data-testid="packages-filter-occasion" className={selCls} value={occasion} onChange={(e) => setOccasion(e.target.value)}>
            {occasions.map((o) => <option key={o} value={o}>{o === "All" ? "All occasions" : o}</option>)}
          </select>
          <select data-testid="packages-filter-budget" className={selCls} value={maxBudget} onChange={(e) => setMaxBudget(Number(e.target.value))}>
            {BUDGET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {activeFilters > 0 && (
            <button data-testid="packages-filter-clear" onClick={clearFilters} className="flex items-center gap-1 h-10 px-3 text-sm font-semibold text-primary hover:bg-primary/5 rounded-full">
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          )}
          <span className="text-sm text-muted-foreground ml-auto" data-testid="packages-count">{filtered.length} {filtered.length === 1 ? "package" : "packages"}</span>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Loading packages...</p>
      ) : packages.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="no-packages">
          No packages available yet. Please check back soon.
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="packages-no-match">
          No packages match your filters. <button onClick={clearFilters} className="text-primary font-semibold underline">Clear filters</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {filtered.map((p, i) => <PackageCard key={p.id} p={p} index={i} />)}
        </div>
      )}
    </div>
  );
}

function Inclusion({ icon: Icon, label, text }) {
  return (
    <div className="flex gap-2">
      <Icon className="h-4 w-4 text-primary shrink-0 mt-0.5" />
      <p><span className="font-semibold text-foreground">{label}:</span> <span className="text-muted-foreground">{text}</span></p>
    </div>
  );
}
